/**
 * CLICAR EM REGIÕES — marcar, numa figura dividida em partes, todas as que cumprem o
 * pedido.
 *
 * A figura é uma `Scene` comum; por cima dela vai uma camada de polígonos, um por
 * região, cada um uma caixa de marcar (role="checkbox"): clique, toque, ou Tab até ela
 * e Espaço. Marcar de novo desmarca. Depois do envio, a camada mostra a correção sem
 * esconder a resposta: o que foi marcado certo, o que foi marcado a mais e o que faltou.
 */

import { scene } from '../render/scene.ts';
import { conversor } from './coords.ts';
import { NOMES } from '../core/lexico.ts';
import { adjetivo, conta, nome } from '../core/pt.ts';
import { renderScene } from '../render/svg.ts';
import type { RegionSelectInteraction } from '../schema/item.ts';

const NS = 'http://www.w3.org/2000/svg';

export interface SeletorRegioes {
  readonly element: HTMLElement;
  selecionadas(): string[];
  /** O aluno chegou a marcar alguma coisa? Sem isso, não há resposta. */
  tocado(): boolean;
  travar(): void;
  marcar(chave: readonly string[]): void;
}

export function criarSeletorRegioes(
  inter: Pick<RegionSelectInteraction, 'descricao' | 'backdrop' | 'viewBox' | 'regioes' | 'oQue'>,
  inicial: readonly string[],
  bloqueadoDesdeInicio: boolean,
): SeletorRegioes {
  const vb = inter.viewBox;
  const sel = new Set(inicial);
  let tocou = false;
  let bloqueado = bloqueadoDesdeInicio;

  const root = document.createElement('div');
  root.className = `regioes${bloqueado ? ' is-bloqueado' : ''}`;
  const svg = renderScene(
    scene(inter.backdrop, {
      viewBox: vb,
      a11y: { title: 'Figura com partes para marcar', desc: inter.descricao },
    }),
  );
  root.appendChild(svg);

  // A camada fica fora do grupo invertido da cena: o y é convertido aqui.
  const { yTela } = conversor(svg, vb);
  const camada = document.createElementNS(NS, 'g');
  camada.setAttribute('class', 'regioes-camada');

  const dica = document.createElement('p');
  dica.className = 'regioes-dica';
  const atualizarDica = (): void => {
    const n = sel.size;
    // "nenhuma casa marcada", "1 polígono marcado", "3 figuras marcadas".
    const marcadas =
      n === 0
        ? `${adjetivo('nenhum', NOMES[inter.oQue].g, 'sg')} ${nome(inter.oQue).adj('marcado')}`
        : String(conta(n, inter.oQue).adj('marcado'));
    dica.textContent = bloqueado
      ? `Resposta enviada: ${marcadas}.`
      : // O comando já pede os cliques; a dica diz o que ele não diz, e a contagem.
        `Um novo clique desmarca. Agora: ${marcadas}.`;
  };

  const polys = inter.regioes.map((r) => {
    const p = document.createElementNS(NS, 'polygon');
    p.setAttribute('class', 'regiao');
    p.setAttribute('points', r.forma.map((q) => `${q.x},${yTela(q.y)}`).join(' '));
    p.setAttribute('role', 'checkbox');
    p.setAttribute('tabindex', bloqueado ? '-1' : '0');
    p.setAttribute('aria-label', r.nome);
    p.setAttribute('aria-checked', String(sel.has(r.id)));
    const alternar = (): void => {
      if (bloqueado) return;
      if (sel.has(r.id)) sel.delete(r.id);
      else sel.add(r.id);
      tocou = true;
      p.setAttribute('aria-checked', String(sel.has(r.id)));
      atualizarDica();
    };
    p.addEventListener('click', alternar);
    p.addEventListener('keydown', (e) => {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      e.preventDefault();
      alternar();
    });
    camada.appendChild(p);
    return { r, p };
  });
  svg.appendChild(camada);
  root.appendChild(dica);
  atualizarDica();

  return {
    element: root,
    selecionadas: () => inter.regioes.map((r) => r.id).filter((id) => sel.has(id)),
    tocado: () => tocou,
    travar() {
      bloqueado = true;
      root.classList.add('is-bloqueado');
      for (const { p } of polys) p.setAttribute('tabindex', '-1');
      atualizarDica();
    },
    marcar(chave) {
      const certa = new Set(chave);
      for (const { r, p } of polys) {
        const marcada = sel.has(r.id);
        p.classList.toggle('is-ok', marcada && certa.has(r.id));
        p.classList.toggle('is-err', marcada && !certa.has(r.id));
        p.classList.toggle('is-falta', !marcada && certa.has(r.id));
      }
    },
  };
}
